import React, { useCallback, useState } from "react";
import { z } from "zod";
import { api, extractApiError } from "@/lib/api";
import { toast } from "sonner";
import { Save, Loader2 } from "lucide-react";
import { useT } from "@/lib/LanguageContext";
import PracticeNameCard from "./settings/PracticeNameCard";
import NotificationSettingsCard from "./settings/NotificationSettingsCard";
import ClientBookingPolicyCard from "./settings/ClientBookingPolicyCard";
import ProfessionalMembershipCard from "./settings/ProfessionalMembershipCard";
// Relative on purpose: no "@" alias on the Jest side (cf. LanguageAwareField.jsx).
import useFieldErrors from "../hooks/useFieldErrors";
import { optionalEmailField } from "../lib/validation";
import FieldError, { invalidInputClass } from "../components/ui/FieldError";
import { cn } from "../lib/utils";
import useAdminLoad, { payloadShapeError } from "../hooks/useAdminLoad";
import LoadFailure from "../components/ui/LoadFailure";

/**
 * POST /admin/settings takes a `Dict[str, Any]` and only whitelists known KEYS — it
 * validates no VALUE. So the one value with a downstream contract is judged here:
 * `business_email` is the address every transactional email is resolved from (the
 * practitioner's copy of a booking, a receipt, a contact-form relay). A typo in it is
 * stored happily and then silently drops that mail — the failure surfaces days later, at
 * the wrong end. Empty is allowed: the resolver falls back to the account's own email.
 */
const settingsSchema = z.object({ business_email: optionalEmailField });

export default function SettingsAdmin() {
  const t = useT("admin");
  const tv = useT("common");
  const { fieldErrors, validate, clearFieldError } = useFieldErrors();
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/settings");
    if (!data || typeof data !== "object" || Array.isArray(data)) throw payloadShapeError();
    setSettings(data);
  }, []);
  // BUG-051 — a failed read used to leave `settings` at `null` forever, and the early
  // return below then rendered a spinner that never stops: no message, no retry, nothing
  // to act on. The failure is now the screen's own third state.
  const { loadError, reload } = useAdminLoad(load);

  // Still reading, and nothing has gone wrong yet.
  if (!settings && !loadError) return <Loader2 className="animate-spin" size={18} />;

  const save = async () => {
    // Normalized, not passed raw: an absent key is `undefined`, and "no address yet" must
    // read as the empty string the optional rule accepts, not as an invalid address.
    if (validate(settingsSchema, { business_email: settings.business_email || "" })) {
      toast.error(tv("validation.fixHighlighted"));
      return;
    }
    setBusy(true); setDone(false);
    try {
      const { data } = await api.post("/admin/settings", settings);
      setSettings(data);
      setDone(true);
      setTimeout(() => setDone(false), 2000);
    } catch (e) {
      toast.error(extractApiError(e, tv("errors.saveFailed"), tv));
    } finally { setBusy(false); }
  };

  const upd = (k) => (e) => { setSettings({ ...settings, [k]: e.target.value }); clearFieldError(k); };

  return (
    <div data-testid="admin-settings">
      <h1 className="font-display text-4xl">{t("settings.title")}</h1>
      <p className="text-eh-muted mt-1">{t("settings.subtitle")}</p>

      <LoadFailure
        message={loadError ? extractApiError(loadError, tv("errors.loadFailed"), tv) : ""}
        onRetry={reload}
        retryLabel={tv("actions.retry")}
        testId="admin-settings-load-error"
        retryTestId="admin-settings-retry"
        className="mt-8 max-w-3xl"
      />

      {/* The two cards below own their own reads, so this screen's failure withdraws only
          the fields it could not read — it does not blank the rest of the page. */}
      {settings && (
      <div className="mt-8 bg-eh-cream border border-eh-border-soft rounded-3xl p-7 space-y-5 max-w-3xl">
        <Field label={t("settings.calendly")}>
          <input value={settings.calendly_url || ""} onChange={upd("calendly_url")} className="eh-fld" placeholder="https://calendly.com/..." />
        </Field>
        <Field label={t("settings.email")}>
          <input
            type="email"
            value={settings.business_email || ""}
            onChange={upd("business_email")}
            aria-invalid={fieldErrors.business_email ? true : undefined}
            className={cn("eh-fld", fieldErrors.business_email && invalidInputClass)}
            data-testid="settings-business-email"
          />
          <FieldError
            message={fieldErrors.business_email ? tv(fieldErrors.business_email) : ""}
            testId="settings-business-email-error"
          />
        </Field>
        <Field label={t("settings.phone")}>
          <input value={settings.business_phone || ""} onChange={upd("business_phone")} className="eh-fld" />
        </Field>
        <Field label={t("settings.shopEnabled")}>
          <label className="flex items-center gap-2"><input type="checkbox" checked={!!settings.shop_enabled} onChange={(e) => setSettings({ ...settings, shop_enabled: e.target.checked })} className="w-4 h-4 accent-eh-sage-deep" /> {settings.shop_enabled ? t("settings.yes") : t("settings.no")}</label>
        </Field>
        <Field label={t("settings.bookingTermsFr")}>
          <textarea rows={3} value={settings.booking_terms_fr || ""} onChange={upd("booking_terms_fr")} className="eh-fld resize-none" />
        </Field>
        <Field label={t("settings.bookingTermsEn")}>
          <textarea rows={3} value={settings.booking_terms_en || ""} onChange={upd("booking_terms_en")} className="eh-fld resize-none" />
        </Field>
        <div className="flex items-center gap-3">
          <button onClick={save} disabled={busy} className="btn-primary text-sm" data-testid="settings-save">{busy ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} {t("actions.save")}</button>
          {done && <span className="text-sm text-eh-sage-deep">✓ {t("actions.saved")}</span>}
        </div>
        <style>{`.eh-fld{width:100%;background:#fff;border:1px solid #E5DEC9;border-radius:12px;padding:9px 12px;font-size:14px;color:#2C3531;outline:none}.eh-fld:focus{border-color:#6F7E5C}`}</style>
      </div>
      )}

      <PracticeNameCard />
      <NotificationSettingsCard />
      <ClientBookingPolicyCard />
      <ProfessionalMembershipCard />
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs uppercase tracking-[0.18em] text-eh-muted block mb-1">{label}</label>
    {children}
  </div>
);
